import { AlertTriangle, CreditCard, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';

interface RegisterLimitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  errorMessage?: string;
}

/**
 * Dialog shown when device approval fails due to register license limit
 */
export function RegisterLimitDialog({
  open,
  onOpenChange,
  errorMessage,
}: RegisterLimitDialogProps) {
  const navigate = useNavigate();
  const { profile } = useAuth();

  // Check if user has billing access (superadmin or primary_admin)
  const hasBillingAccess = profile?.role === 'superadmin' || profile?.role === 'primary_admin';

  // Extract the number of licensed registers from the error message
  const licensedCount = errorMessage?.match(/You have (\d+) licensed/)?.[1] || '?';

  const handleGoToBilling = () => {
    onOpenChange(false);
    navigate('/settings?section=billing');
  };

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent maxWidth="sm">
        <ResponsiveModalHeader>
          <div className="mx-auto w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mb-2">
            <AlertTriangle className="h-6 w-6 text-amber-600" />
          </div>
          <ResponsiveModalTitle className="text-center">
            Register Limit Reached
          </ResponsiveModalTitle>
          <ResponsiveModalDescription className="text-center">
            {hasBillingAccess
              ? `Your subscription includes ${licensedCount} register licenses. To add more devices, please upgrade your plan.`
              : `Your organization has reached its limit of ${licensedCount} registered devices.`}
          </ResponsiveModalDescription>
        </ResponsiveModalHeader>

        <ResponsiveModalBody className="space-y-4">
          <div className="bg-muted/50 rounded-xl border p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <CreditCard className="h-5 w-5 text-primary" />
              </div>
              <div>
                {hasBillingAccess ? (
                  <>
                    <p className="font-medium">Upgrade Your Plan</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Additional register licenses can be purchased from the billing settings.
                      Each license allows you to register one more POS terminal or device.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium">Contact Your Administrator</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Please contact your organization administrator or account owner
                      to upgrade the subscription and add more register licenses.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </ResponsiveModalBody>

        <ResponsiveModalFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
          {hasBillingAccess && (
            <Button
              variant="blaze"
              className="flex-1"
              onClick={handleGoToBilling}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Go to Billing
            </Button>
          )}
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}

/**
 * Helper to check if an error is a register limit error
 */
export function isRegisterLimitError(error: unknown): boolean {
  if (!error) return false;
  const message = error instanceof Error ? error.message : String(error);
  return message.toLowerCase().includes('register limit reached');
}
